import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Leaf, AlertCircle, EuroIcon, Check, Package, Tag, Sparkles } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { BulkrrLogo } from '../../shared/components/BulkrrLogo';
import { generateId } from '../../shared/utils/id';
const COMMON_STORES = [{
  name: 'Albert Heijn',
  color: '#00A0E2'
}, {
  name: 'Jumbo',
  color: '#FFC800'
}, {
  name: 'Lidl',
  color: '#0050AA'
}, {
  name: 'Aldi',
  color: '#0000FF'
}, {
  name: 'Plus',
  color: '#78C142'
}, {
  name: 'Dirk',
  color: '#E30613'
}];
const COMMON_PRODUCTS = [{
  name: 'Melk',
  category: 'Zuivel',
  unit: 'l',
  defaultQty: 2
}, {
  name: 'Brood',
  category: 'Bakkerij',
  unit: 'stuk',
  defaultQty: 1
}, {
  name: 'Eieren',
  category: 'Zuivel',
  unit: 'stuk',
  defaultQty: 10
}, {
  name: 'Koffie',
  category: 'Voorraadkast',
  unit: 'pak',
  defaultQty: 1
}, {
  name: 'Aardappelen',
  category: 'Groenten',
  unit: 'kg',
  defaultQty: 2
}, {
  name: 'Pasta',
  category: 'Voorraadkast',
  unit: 'pak',
  defaultQty: 2
}, {
  name: 'Kaas',
  category: 'Zuivel',
  unit: 'stuk',
  defaultQty: 1
}, {
  name: 'Wc-papier',
  category: 'Overig',
  unit: 'pak',
  defaultQty: 1
}];
export function OnboardingFlow() {
  const [step, setStep] = useState(1);
  const {
    updateSettings,
    setOnboardingCompleted,
    setStores,
    setMonthlyBudget,
    addInventoryItem
  } = useAppStore();
  // Form state
  const [householdSize, setHouseholdSize] = useState(2);
  const [dietary, setDietary] = useState<string[]>([]);
  const [budget, setBudget] = useState(400);
  const [selectedStores, setSelectedStores] = useState<string[]>(['Albert Heijn']);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const handleNext = () => {
    if (step < 6) setStep(step + 1);else finishOnboarding();
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  const finishOnboarding = () => {
    // Save settings
    updateSettings({
      householdSize,
      dietaryPreferences: dietary
    });
    setMonthlyBudget(budget);
    // Save stores
    const storesToSave = selectedStores.map(storeName => {
      const storeInfo = COMMON_STORES.find(s => s.name === storeName);
      return {
        id: generateId(),
        name: storeName,
        color: storeInfo?.color || '#6366f1',
        type: 'supermarket' as const,
        isActive: true
      };
    });
    setStores(storesToSave);
    // Save products
    selectedProducts.forEach(productName => {
      const productInfo = COMMON_PRODUCTS.find(p => p.name === productName);
      if (productInfo) {
        const multiplier = householdSize > 2 ? 2 : 1;
        const qty = productInfo.defaultQty * multiplier;
        addInventoryItem({
          id: generateId(),
          name: productInfo.name,
          category: productInfo.category,
          quantity: qty,
          unit: productInfo.unit,
          minQuantity: Math.max(1, Math.floor(qty / 2)),
          maxQuantity: qty * 2,
          location: 'Voorraadkast',
          lastRestocked: new Date().toISOString(),
          price: 2.5,
          bulkEligible: true,
          averageShelfLife: 14,
          consumptionRate: 0,
          storageRequirement: 1,
          currentStock: qty,
          totalWeight: 0,
          stockStatus: 'medium'
        });
      }
    });
    setOnboardingCompleted(true);
  };
  const toggleDietary = (pref: string) => {
    setDietary(prev => prev.includes(pref) ? prev.filter(p => p !== pref) : [...prev, pref]);
  };
  const toggleStore = (store: string) => {
    setSelectedStores(prev => prev.includes(store) ? prev.filter(s => s !== store) : [...prev, store]);
  };
  const toggleProduct = (product: string) => {
    setSelectedProducts(prev => prev.includes(product) ? prev.filter(p => p !== product) : [...prev, product]);
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  return <div className="fixed inset-0 bg-background z-50 flex flex-col" data-id="element-1836" data-mp="hp8o5jqefbd9">
      {/* Progress Bar */}
      <div className="h-1 bg-secondary w-full" data-id="element-1837" data-mp="vozvj1mettia">
        <motion.div className="h-full bg-primary" initial={{
        width: '0%'
      }} animate={{
        width: `${step / 6 * 100}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-1838" data-mp="vprridjx5t93" />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden relative" data-id="element-1839" data-mp="2240rv8fckdpe">
        <AnimatePresence mode="wait" custom={1} data-id="element-1840" data-mp="2alb4bxij7sgr">
          {step === 1 && <motion.div key="step1" variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center" data-id="element-1841" data-mp="2emwh7iv2g6qy">
              <BulkrrLogo size="xl" className="mb-8" data-id="element-1842" data-mp="2tc2f93i63qx9" />
              <h1 className="text-3xl font-bold text-foreground mb-4" data-id="element-1843" data-mp="2tckfscbv4raz">
                Welkom bij Bulkrr
              </h1>
              <p className="mb-12 max-w-xs text-lg text-muted-foreground" data-id="element-1844" data-mp="2ycyeh3rkqowc">
                Houd bij wat je in huis hebt, maak makkelijker je lijst en zie waar je geld naartoe gaat.
              </p>
              <button onClick={handleNext} className="w-full max-w-xs py-4 bg-primary text-primary-foreground rounded-2xl font-bold text-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2" data-id="element-1845" data-mp="2oizfgdmsrl8m">
                Laten we beginnen <ChevronRight className="w-5 h-5" data-id="element-1846" data-mp="3qwu2zhx57x0e" />
              </button>
            </motion.div>}

          {step === 2 && <motion.div key="step2" variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex flex-col p-6 pt-12" data-id="element-1847" data-mp="3gz5y3z5tp94i">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-1848" data-mp="3802canf9ufmb">
                Jouw Huishouden
              </h2>
              <p className="text-muted-foreground mb-8" data-id="element-1849" data-mp="3367vbrr1weqx">
                Dit helpt ons om verbruik beter in te schatten.
              </p>

              <div className="space-y-6" data-id="element-1850" data-mp="2tamq7kqoqycy">
                <div data-id="element-1851" data-mp="2t7s47os0x5hf">
                  <label className="text-sm font-semibold text-foreground mb-3 block" data-id="element-1852" data-mp="2p89ttmxtwcsu">
                    Hoeveel personen?
                  </label>
                  <div className="grid grid-cols-4 gap-3" data-id="element-1853" data-mp="2kahrdyzk58ae">
                    {[1, 2, 3, 4].map(num => <button key={num} onClick={() => {
                  setHouseholdSize(num);
                  setBudget(num * 200); // Smart default
                }} className={`py-4 rounded-2xl border-2 font-bold text-lg transition-all ${householdSize === num ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-foreground hover:border-primary/50'}`} data-id="element-1854" data-mp="zqmiqv6luphp">
                        {num}
                        {num === 4 ? '+' : ''}
                      </button>)}
                  </div>
                </div>

                <div data-id="element-1855" data-mp="ut2zfgv1vmiw">
                  <label className="text-sm font-semibold text-foreground mb-3 block" data-id="element-1856" data-mp="utchmi9uspe1">
                    Dieetwensen (optioneel)
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1857" data-mp="2b8mn6unv522">
                    <button onClick={() => toggleDietary('vega')} className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${dietary.includes('vega') ? 'border-success bg-success/10 text-success' : 'border-border bg-card text-foreground'}`} data-id="element-1858" data-mp="29dxfgccwqs6">
                      <Leaf className="w-6 h-6" data-id="element-1859" data-mp="2wsdlzih2yb81" />
                      <span className="font-medium text-sm" data-id="element-1860" data-mp="2wrlsqeaygpkx">Vegetarisch</span>
                    </button>
                    <button onClick={() => toggleDietary('gluten')} className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${dietary.includes('gluten') ? 'border-warning bg-warning/10 text-warning' : 'border-border bg-card text-foreground'}`} data-id="element-1861" data-mp="3aqlcb7jm2ea8">
                      <AlertCircle className="w-6 h-6" data-id="element-1862" data-mp="3timm34xxboyf" />
                      <span className="font-medium text-sm" data-id="element-1863" data-mp="3th35tec9pdwn">Glutenvrij</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>}

          {step === 3 && <motion.div key="step3" variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex flex-col p-6 pt-12" data-id="element-1864" data-mp="gzbkil1u5u9g">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-1865" data-mp="6zkogexmn779">
                Maandbudget
              </h2>
              <p className="text-muted-foreground mb-8" data-id="element-1866" data-mp="3pdk2kz6qf2nd">
                Wat wil je maximaal uitgeven per maand?
              </p>

              <div className="flex-1 flex flex-col items-center justify-center" data-id="element-1867" data-mp="3fhpdpmbx0bc5">
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mb-8" data-id="element-1868" data-mp="3kg0pi2lrsvx0">
                  <EuroIcon className="w-10 h-10 text-primary" data-id="element-1869" data-mp="36fzehubsk83t" />
                </div>

                <div className="text-5xl font-bold text-foreground mb-8" data-id="element-1870" data-mp="3bfcbv90agxkn">
                  €{budget}
                </div>

                <input type="range" min="100" max="1500" step="50" value={budget} onChange={e => setBudget(parseInt(e.target.value))} className="w-full max-w-xs accent-primary" data-id="element-1871" data-mp="22a41nxwlejq0" />
                <p className="text-sm text-muted-foreground mt-4 text-center" data-id="element-1872" data-mp="1y9btz93dcmvu">
                  Gemiddeld voor {householdSize}{' '}
                  {householdSize === 1 ? 'persoon' : 'personen'} is dit rond de
                  €{householdSize * 200}.
                </p>
              </div>
            </motion.div>}

          {step === 4 && <motion.div key="step4" variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex flex-col p-6 pt-12" data-id="element-1873" data-mp="2h74l7e4mr8de">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-1874" data-mp="39q1tk9daeggh">
                Jouw Winkels
              </h2>
              <p className="mb-8 text-muted-foreground" data-id="element-1875" data-mp="3f15keri6dor0">
                Waar doe je meestal boodschappen? We tonen aanbiedingen die bij deze winkels passen.
              </p>

              <div className="grid grid-cols-2 gap-3" data-id="element-1876" data-mp="3srsmmtn59wru">
                {COMMON_STORES.map(store => <button key={store.name} onClick={() => toggleStore(store.name)} className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${selectedStores.includes(store.name) ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'}`} data-id="element-1877" data-mp="3nvk8njkoo9m9">
                    <div className="w-4 h-4 rounded-full" style={{
                backgroundColor: store.color
              }} data-id="element-1878" data-mp="351ybcgih4g9p" />
                    <span className="font-medium text-foreground" data-id="element-1879" data-mp="kc8tdumh4ecs">
                      {store.name}
                    </span>
                    {selectedStores.includes(store.name) && <Check className="w-4 h-4 text-primary ml-auto" data-id="element-1880" data-mp="wdqkgqs6yz1s" />}
                  </button>)}
              </div>
            </motion.div>}

          {step === 5 && <motion.div key="step5" variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex flex-col p-6 pt-12" data-id="element-1881" data-mp="16cg3imvtqpjm">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-1882" data-mp="indciofavkcg">
                Eerste Producten
              </h2>
              <p className="mb-2 text-muted-foreground" data-id="element-1883" data-mp="nfy5xouanvfg">
                Selecteer wat je nu al in huis hebt. Zo weet je later wat je moet kopen.
              </p>
              <p className="mb-8 text-sm text-muted-foreground" data-id="element-1884" data-mp="3r2x29dgrikmr">
                Weet je het nog niet? Geen probleem, je kunt dit later toevoegen.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 overflow-y-auto pb-24" data-id="element-1885" data-mp="3jhl8bfpvhl0d">
                {COMMON_PRODUCTS.map(product => <button key={product.name} onClick={() => toggleProduct(product.name)} className={`p-4 rounded-2xl border-2 text-left transition-all ${selectedProducts.includes(product.name) ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/50'}`} data-id="element-1886" data-mp="39kq9evpotilk">
                    <div className="flex justify-between items-start mb-2" data-id="element-1887" data-mp="2qv9wf8wtfxo9">
                      <Package className={`w-5 h-5 ${selectedProducts.includes(product.name) ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-1888" data-mp="2qt5u2vekf6q3" />
                      {selectedProducts.includes(product.name) && <Check className="w-4 h-4 text-primary" data-id="element-1889" data-mp="2cv8ya17xr4ch" />}
                    </div>
                    <span className="font-medium text-foreground block" data-id="element-1890" data-mp="31ng672fbhq02">
                      {product.name}
                    </span>
                    <span className="text-xs text-muted-foreground" data-id="element-1891" data-mp="36n2jfbv5b68v">
                      {product.category}
                    </span>
                  </button>)}
              </div>
            </motion.div>}

          {step === 6 && <motion.div key="step6" variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex flex-col p-6 pt-12" data-id="element-1892" data-mp="2iuvu2wnu7ziy">
              <h2 className="text-2xl font-bold text-foreground mb-8 text-center" data-id="element-1893" data-mp="2dwaoehptgnn4">
                Hoe het werkt
              </h2>

              <div className="space-y-8 flex-1" data-id="element-1894" data-mp="84xa4mflwuqv">
                <div className="flex items-start gap-4" data-id="element-1895" data-mp="c57ejunr3d3m">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-1896" data-mp="c4nvubqa0koj">
                    <Package className="w-6 h-6 text-primary" data-id="element-1897" data-mp="1wnhwjjxiex3q" />
                  </div>
                  <div data-id="element-1898" data-mp="1sn7u2h8xeotm">
                    <h3 className="font-bold text-foreground text-lg" data-id="element-1899" data-mp="1sjtoz18vo1ih">
                      1. Hou voorraad bij
                    </h3>
                    <p className="text-muted-foreground" data-id="element-1900" data-mp="21ltao6uu7trr">
                      Scan of voeg producten toe. Wij houden bij wat er bijna op
                      is.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-1901" data-mp="2bbmgssc7ngom">
                  <div className="w-12 h-12 rounded-2xl bg-success/10 flex items-center justify-center shrink-0" data-id="element-1902" data-mp="2pcffzqdgizy2">
                    <Tag className="w-6 h-6 text-success" data-id="element-1903" data-mp="2kckl7bqk9q7q" />
                  </div>
                  <div data-id="element-1904" data-mp="uxcsfy7yxrxs">
                    <h3 className="font-bold text-foreground text-lg" data-id="element-1905" data-mp="uyxra01bfx1g">
                      2. Bekijk aanbiedingen
                    </h3>
                    <p className="text-muted-foreground" data-id="element-1906" data-mp="q15xf0h420um">
                      Zie welke aanbiedingen er zijn bij de winkels die jij hebt gekozen.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-1907" data-mp="2zz8l4ati42wx">
                  <div className="w-12 h-12 rounded-2xl bg-warning/10 flex items-center justify-center shrink-0" data-id="element-1908" data-mp="2zxnpsizd4fy0">
                    <Sparkles className="w-6 h-6 text-warning" data-id="element-1909" data-mp="33yppt213sw11" />
                  </div>
                  <div data-id="element-1910" data-mp="38zn596uqbbyu">
                    <h3 className="font-bold text-foreground text-lg" data-id="element-1911" data-mp="38y2iw4ikizc2">
                      3. Bespaar geld
                    </h3>
                    <p className="text-muted-foreground" data-id="element-1912" data-mp="3ipghma2p3v45">
                      Koop slim in bulk wanneer het goedkoop is, en blijf binnen
                      budget.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Bottom Navigation */}
      {step > 1 && <div className="p-6 bg-background border-t border-border flex gap-4" data-id="element-1913" data-mp="1l1w21co6tlhs">
          <button onClick={handleBack} className="p-4 rounded-2xl border border-border text-foreground hover:bg-accent transition-colors" data-id="element-1914" data-mp="1kzru8jn642c8">
            <ChevronLeft className="w-6 h-6" data-id="element-1915" data-mp="1c1pu06b9ddq0" />
          </button>
          <button onClick={handleNext} disabled={step === 4 && selectedStores.length === 0} className="flex-1 py-4 bg-primary text-primary-foreground rounded-2xl font-bold text-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1916" data-mp="173elq00m3b9w">
            {step === 6 ? 'Naar mijn overzicht' : step === 5 && selectedProducts.length === 0 ? 'Later toevoegen' : 'Volgende'}
          </button>
        </div>}
    </div>;
}